import { useState } from "react";
import { MapPin, Pencil, Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AddressDialog } from "@/components/AddressDialog";
import { api, formatApiError } from "@/lib/api";

const input = "mt-2 w-full h-12 bg-white/5 border border-white/15 focus:border-[#FF3B30] outline-none px-4 transition-colors";
const Section = ({ eyebrow, title, children, action }) => (
  <section className="border border-white/10 bg-[#121215] p-6 sm:p-8">
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] font-semibold">{eyebrow}</p>
        <h2 className="font-display font-black uppercase tracking-tight text-2xl mt-1">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
);
const Btn = ({ children, ...p }) => <button {...p} className={`h-11 px-6 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider disabled:opacity-60 ${p.className || ""}`}>{children}</button>;

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState({ open: false, address: null });

  const saveName = async (e) => {
    e.preventDefault(); setBusy("name");
    try { const { data } = await api.put("/auth/profile", { name }); setUser(data); toast.success("Profile updated"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("New passwords do not match");
    setBusy("pw");
    try {
      await api.put("/auth/password", { current_password: pw.current_password, new_password: pw.new_password });
      setUser({ ...user, has_password: true }); setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success(user.has_password ? "Password changed" : "Password set — you can now sign in with email too");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };

  const addrAction = async (fn, msg) => {
    try { const { data } = await fn(); setUser(data); toast.success(msg); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12" data-testid="profile-page">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold mb-2">Account</p>
      <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl">My profile</h1>
      <p className="font-mono text-xs text-neutral-500 mt-3" data-testid="profile-email">{user.email} · {user.auth_provider === "google" ? "GOOGLE ACCOUNT" : "EMAIL ACCOUNT"}</p>

      <div className="mt-10 space-y-6">
        <Section eyebrow="Identity" title="Details">
          <form onSubmit={saveName} className="flex flex-col sm:flex-row sm:items-end gap-4">
            <label className="block flex-1"><span className="font-mono text-xs uppercase tracking-widest text-neutral-400">Display name</span>
              <input data-testid="profile-name-input" required maxLength={80} className={input} value={name} onChange={(e) => setName(e.target.value)} /></label>
            <Btn data-testid="profile-name-submit" disabled={busy === "name" || name.trim() === user.name}>Save</Btn>
          </form>
        </Section>

        <Section eyebrow="Security" title={user.has_password ? "Change password" : "Set a password"}>
          <form onSubmit={savePw} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            {user.has_password && <label className="block"><span className="font-mono text-xs uppercase tracking-widest text-neutral-400">Current</span>
              <input data-testid="profile-current-password" type="password" required className={input} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></label>}
            <label className="block"><span className="font-mono text-xs uppercase tracking-widest text-neutral-400">New password</span>
              <input data-testid="profile-new-password" type="password" required minLength={6} className={input} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></label>
            <label className="block"><span className="font-mono text-xs uppercase tracking-widest text-neutral-400">Confirm</span>
              <input data-testid="profile-confirm-password" type="password" required minLength={6} className={input} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></label>
            <Btn data-testid="profile-password-submit" disabled={busy === "pw"} className="sm:col-start-3">{user.has_password ? "Update password" : "Set password"}</Btn>
          </form>
        </Section>

        <Section eyebrow="Shipping" title="Saved addresses"
          action={<button data-testid="address-add-button" onClick={() => setDialog({ open: true, address: null })} className="h-10 px-4 border border-white/20 hover:bg-white hover:text-black font-display uppercase font-bold tracking-wider text-sm flex items-center gap-2"><Plus className="w-4 h-4" />Add</button>}>
          {user.addresses?.length === 0 && <p data-testid="addresses-empty" className="text-sm text-neutral-500">No addresses yet. Save one to speed up checkout.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user.addresses?.map((a) => (
              <div key={a.id} data-testid={`address-card-${a.id}`} className={`relative border p-5 ${a.is_default ? "border-[#FF3B30]/60 bg-[#FF3B30]/5" : "border-white/10 bg-black/30"}`}>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-[#FF3B30]" />
                  <span className="font-display uppercase font-bold">{a.label}</span>
                  {a.is_default && <span data-testid={`address-default-badge-${a.id}`} className="font-mono text-[9px] uppercase text-[#FF3B30] border border-[#FF3B30]/40 px-1">Default</span>}
                </div>
                <p className="text-sm">{a.name}</p>
                <p className="text-sm text-neutral-400">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                <p className="text-sm text-neutral-400">{[a.city, a.state, a.postal_code].filter(Boolean).join(" ")} · {a.country}</p>
                {a.phone && <p className="font-mono text-xs text-neutral-500 mt-1">{a.phone}</p>}
                <div className="flex gap-1 mt-4 -ml-2">
                  {!a.is_default && <button data-testid={`address-set-default-${a.id}`} onClick={() => addrAction(() => api.put(`/auth/addresses/${a.id}/default`), "Default address updated")} className="p-2 text-neutral-400 hover:text-white flex items-center gap-1 font-mono text-[10px] uppercase"><Star className="w-3.5 h-3.5" />Make default</button>}
                  <button data-testid={`address-edit-${a.id}`} onClick={() => setDialog({ open: true, address: a })} className="p-2 text-neutral-400 hover:text-white"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`address-delete-${a.id}`} onClick={() => addrAction(() => api.delete(`/auth/addresses/${a.id}`), "Address removed")} className="p-2 text-neutral-400 hover:text-[#FF3B30]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
      <AddressDialog open={dialog.open} onOpenChange={(v) => setDialog({ ...dialog, open: v })} address={dialog.address} />
    </div>
  );
}
